import styled from 'styled-components';
import { getContrastColor } from '../../utils/colorUtils';
import { tooltipMixin } from '../../styles/tooltipMixin';

export const DEFAULT_IMG = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' rx='10' fill='%23333'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='52' fill='%23666'%3E%F0%9F%93%A6%3C/text%3E%3C/svg%3E`;

export const PageWrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 30px 20px 60px;
  min-height: 80vh;
`;

export const Card = styled.div`
  background: ${p => p.bg || 'transparent'};
  border: 1px solid ${p => p.border || '#333'};
  border-radius: 12px;
  padding: ${p => p.pad || '24px'};
  box-shadow: 0 4px 20px rgba(0,0,0,0.35);
  transition: background-color 0.3s;
`;

export const SectionTitle = styled.h2`
  color: ${p => p.accent};
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 18px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${p => p.accent}44;
`;

export const HeaderCard = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  margin-bottom: 20px;
  align-items: start;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

export const UserInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

export const AvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 80px;
  height: 80px;
`;

export const AvatarImg = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid ${p => p.accent};
  object-fit: cover;
  display: block;
`;

export const AvatarPresetGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; max-width: 280px;
`;

export const AvatarPresetBtn = styled.button`
  width: 52px; height: 52px; border-radius: 50%;
  background: transparent;
  border: 2px solid transparent;
  padding: 2px;
  cursor: pointer;
  transition: transform 0.15s, border-color 0.15s;
  &:hover { transform: scale(1.12); border-color: white; }
`;

export const AvatarEditOverlay = styled.label`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
  font-size: 22px;
  ${AvatarWrapper}:hover & { opacity: 1; }
`;

export const UserMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const UserName = styled.h1`
  color: ${p => getContrastColor(p.bg)};
  font-size: 22px;
  font-weight: 700;
  margin: 0;
`;

export const UserEmail = styled.p`
  color: ${p => getContrastColor(p.bg)}aa;
  font-size: 14px;
  margin: 0;
`;

export const TipoBadge = styled.span`
  display: inline-block;
  margin-top: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${p => p.accent}22;
  color: ${p => p.accent};
  border: 1px solid ${p => p.accent}55;
  text-transform: capitalize;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 6px;
`;

export const RankingPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  min-width: 300px;

  @media (max-width: 600px) { min-width: unset; width: 100%; }
`;

export const RankBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${p => p.bg}cc;
  border: 1px solid ${p => p.border || '#333'};
  border-radius: 8px;
  padding: 8px 12px;
`;

export const RankIcon  = styled.span`font-size: 18px; line-height: 1;`;
export const RankInfo  = styled.div`display: flex; flex-direction: column;`;

export const RankLabel = styled.span`
  font-size: 11px;
  color: ${p => getContrastColor(p.bg)}88;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const RankValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.accent};
  line-height: 1.2;
`;

export const BodyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

export const FormGroup = styled.div`margin-bottom: 16px;`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: ${p => getContrastColor(p.bg)}99;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const inputBase = `
  width: 100%;
  padding: 10px 12px;
  border-radius: 7px;
  font-size: 14px;
  box-sizing: border-box;
  transition: all 0.2s;
  font-family: inherit;
`;

export const Input = styled.input`
  ${inputBase}
  background: ${p => p.editing ? (getContrastColor(p.bg) === '#ffffff' ? '#2a2a2a' : '#f0f0f0') : 'transparent'};
  border: ${p => p.editing
    ? `2px solid ${getContrastColor(p.bg) === '#ffffff' ? '#555' : '#ccc'}`
    : '2px solid transparent'};
  color: ${p => getContrastColor(p.bg)};
  cursor: ${p => p.editing ? 'text' : 'default'};
  pointer-events: ${p => p.editing ? 'auto' : 'none'};

  &:focus { outline: none; border-color: ${p => p.accent}; box-shadow: 0 0 0 3px ${p => p.accent}33; }
  &::placeholder { color: ${p => getContrastColor(p.bg)}44; }
`;

export const Select = styled.select`
  ${inputBase}
  background: ${p => p.editing ? (getContrastColor(p.bg) === '#ffffff' ? '#2a2a2a' : '#f0f0f0') : 'transparent'};
  border: ${p => p.editing
    ? `2px solid ${getContrastColor(p.bg) === '#ffffff' ? '#555' : '#ccc'}`
    : '2px solid transparent'};
  color: ${p => getContrastColor(p.bg)};
  cursor: ${p => p.editing ? 'pointer' : 'default'};
  pointer-events: ${p => p.editing ? 'auto' : 'none'};
  appearance: ${p => p.editing ? 'auto' : 'none'};

  &:focus { outline: none; border-color: ${p => p.accent}; box-shadow: 0 0 0 3px ${p => p.accent}33; }
  option { background: #1a1a1a; color: #fff; }
`;

export const Btn = styled.button`
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  ${p => p.variant === 'primary' && `
    background: ${p.accent}; color: #fff;
    &:hover { opacity: 0.85; transform: translateY(-1px); }
  `}
  ${p => p.variant === 'ghost' && `
    background: transparent;
    color: ${getContrastColor(p.bg || '#000')}99;
    border: 1px solid ${getContrastColor(p.bg || '#000')}33;
    &:hover { background: ${getContrastColor(p.bg || '#000')}11; }
  `}
  ${p => p.variant === 'danger' && `
    background: #e74c3c; color: #fff;
    &:hover { background: #c0392b; }
  `}
`;

export const ProtectedScreen = styled.div`
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  min-height: 60vh; gap: 16px; text-align: center;
`;

export const LockIcon       = styled.div`font-size: 56px; margin-bottom: 8px;`;
export const ProtectedTitle = styled.h2`color: ${p => getContrastColor(p.bg)}; font-size: 22px; margin: 0;`;
export const ProtectedSub   = styled.p`color: ${p => getContrastColor(p.bg)}88; font-size: 15px; margin: 0; max-width: 320px;`;

export const DatosGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: start;

  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

export const ImgSlot = styled.div`display: flex; flex-direction: column; align-items: center; gap: 8px;`;

export const ImgSlotImg = styled.img`
  width: 120px; height: 120px; border-radius: 10px;
  object-fit: cover; border: 2px solid ${p => p.accent};
`;

export const ImgSlotLabel = styled.label`
  display: inline-block;
  padding: 7px 14px; border-radius: 7px;
  font-size: 12px; font-weight: 600; cursor: pointer;
  background: transparent;
  color: ${p => getContrastColor(p.bg || '#000')}99;
  border: 1px solid ${p => getContrastColor(p.bg || '#000')}33;
  transition: all 0.2s; text-align: center;
  &:hover { background: ${p => getContrastColor(p.bg || '#000')}11; }
`;

export const TooltipWrap = styled.div`
  position: relative;
  width: 100%;
 ${tooltipMixin('center')}
`;
